import type { KeyboardEventHandler, Ref } from 'react';
import React from 'react';
import { HBox } from '../../layout';
import type { ValidIconName } from '../../basic';
import { Icon } from '../../basic';
import { formatChatMessageDate } from './chatDateFormatting';
import { intlFmt } from '../../../lib';

export type ChatNoticeMessageProps = {
  text: string;
  date: Date | string;
  icon?: ValidIconName;
  /**
   * Discriminates the kind of room state change this notice represents
   * (e.g. 'memberAdded', 'memberRemoved', 'groupRenamed'), so UI tests can
   * target a specific notice type via `data-testid` without matching on
   * localized text.
   */
  noticeType?: string;
  tabIndex?: number;
  messageRef?: Ref<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
};

export default function ChatNoticeMessage({
  text,
  date,
  icon,
  noticeType,
  tabIndex,
  messageRef,
  onKeyDown,
  onFocus
}: Readonly<ChatNoticeMessageProps>): React.JSX.Element {
  const formattedDate = formatChatMessageDate(date);

  return (
    <HBox
      ref={messageRef}
      data-testid={
        noticeType ? `chat-message-notice-${noticeType}` : 'chat-message-notice'
      }
      className="ehs-chat-message chat-notice-component"
      align="start"
      aria-live="polite"
      role="group"
      tabIndex={tabIndex ?? -1}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    >
      <div className="chat-notice-icon-container">
        {icon && (
          <Icon iconName={icon} aria-label="" className="chat-notice-icon" />
        )}
      </div>
      <div className="chat-notice-body">
        {formattedDate && (
          <>
            <time
              dateTime={formattedDate}
              aria-label={intlFmt('common.chatmessage.sentOn', formattedDate)}
              className="chat-notice-date"
            >
              {formattedDate}
            </time>
            <span className="chat-notice-separator"> • </span>
          </>
        )}
        <span className="chat-notice-text">{text}</span>
      </div>
    </HBox>
  );
}
